import { FunctionComponent, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { faCheck, faCopy } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { copyText } from "@/utils/copyText";

interface CopyBlockProps {
	/** Raw text rendered in the block and copied verbatim to the clipboard. */
	value: string;
	/** Short label shown above the block (e.g. "json", "Endpoint"). */
	label?: string;
	testId?: string;
}

/**
 * Monospace code/value block with a copy-to-clipboard button. Used across the MCP guide for the
 * endpoint, header, and per-client configuration snippets.
 */
export const CopyBlock: FunctionComponent<CopyBlockProps> = ({ value, label, testId }) => {
	const { t } = useTranslation();
	const [copied, setCopied] = useState(false);

	const handleCopy = useCallback(() => {
		void copyText(value).then((copied) => {
			if (!copied) {
				return;
			}

			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		});
	}, [value]);

	return (
		<div className="relative rounded-lg border border-border-light bg-background-light">
			{label && (
				<span className="absolute left-3 top-2 text-xs font-medium uppercase tracking-wide text-text-meta">
					{label}
				</span>
			)}
			<button
				aria-label={t("integrations.mcp.copy")}
				className="absolute right-2 top-2 flex cursor-pointer items-center gap-1.5 rounded-md border border-border-light bg-brand-white px-2 py-1 text-xs font-medium text-text-meta hover:text-text"
				data-testid={testId}
				type="button"
				onClick={handleCopy}
			>
				<Icon icon={copied ? faCheck : faCopy} />
				<span>{copied ? t("integrations.mcp.copied") : t("integrations.mcp.copy")}</span>
			</button>

			<pre
				className={`overflow-x-auto px-4 pb-4 text-xs leading-relaxed text-text ${label ? "pt-9" : "pt-11"}`}
			>
				<code>{value}</code>
			</pre>
		</div>
	);
};
